import { CartApi } from '@common/api/cart';
import { emitCacheCartNumber, emitCartNumber } from '@common/application/utils/cart-number';
import { classNames } from '@common/application/utils/classnames';
import {
  checkPreSaleProduct,
  checkSoldOutProduct,
  checkUnShelveProduct,
  getFirstImage,
  getProductCartCount
} from '@common/application/utils/common';
import { useCartNumberListener } from '@common/application/utils/event';
import { Toast } from '@common/application/utils/ui/toast';
import { Button } from '@common/components/common/button';
import { DImage } from '@common/components/common/image';
import { Price } from '@common/components/common/price';
import type { ProductDetailVO } from '@common/types/product-detail';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';

import { BlackLineIcon } from '@/resources/sprites-images/black-line-icon';

export interface ProductProps {
  product: ProductDetailVO;
  showRemove: boolean;
  onRemove(): void;
}

const DisabledButton: React.FC<React.PropsWithChildren> = ({ children }) => {
  return (
    <Button
      className={classNames(
        'flex-1 h-8 text-black-1 bg-black-7 text-xs border-none',
        'pc:h-9 pc:text-sm'
      )}
      disabled
    >
      {children}
    </Button>
  );
};

export const Product: React.FC<ProductProps> = (props) => {
  const { product, showRemove, onRemove } = props;
  const { simpleProductVo, simplePriceVo } = product;
  const { t } = useTranslation();
  const [cartNumber, setCartNumber] = useState(0);
  const hasLinePrice =
    simplePriceVo.price &&
    simplePriceVo.retailPrice &&
    +simplePriceVo.retailPrice > +simplePriceVo.price;

  useEffect(() => {
    emitCacheCartNumber();
  }, []);
  useCartNumberListener((countStatistics) => {
    setCartNumber(getProductCartCount(simpleProductVo, countStatistics));
  });

  async function addCart() {
    await CartApi.add({
      number: 1,
      goodsSize: simpleProductVo.goodsSize,
      csku: simpleProductVo.csku,
      rsku: simpleProductVo.rsku,
      pageType: '1'
    });
    Toast(t('common:addedToCart'), { type: 'success' });
    emitCartNumber();
  }

  return (
    <div>
      <div className={classNames('p-3 w-37.5 h-69 ', 'pc:p-5 pc:w-62.5 pc:h-97.5')}>
        <DImage
          className={classNames('mb-3 w-31.5 h-31.5', 'pc:mb-5 pc:w-52.5 pc:h-52.5')}
          src={getFirstImage(simpleProductVo.customPictures)!}
          fill
        />
        <p
          className={classNames(
            'mb-2 first-line:w-full h-8.5 text-black-1 text-xs line-clamp-2',
            'pc:h-10 pc:text-sm'
          )}
        >
          {simpleProductVo.cskuName}
        </p>
        <div
          className={classNames(
            'mb-2 first:flex flex-col',
            'pc:flex-row pc:items-center pc:line-clamp-1'
          )}
        >
          <Price
            className={classNames('mr-1 text-base font-medium text-black-1', 'pc:text-xl', {
              'text-primary': hasLinePrice
            })}
            price={simplePriceVo.price}
          />
          <Price
            className={classNames('invisible text-xs text-black-4 line-throug', {
              visible: hasLinePrice
            })}
            price={simplePriceVo.retailPrice ?? '0.00'}
          />
        </div>
        <div className="flex">
          {checkUnShelveProduct(simpleProductVo) ? (
            <DisabledButton>{t('product-diff:unShelve')}</DisabledButton>
          ) : checkSoldOutProduct(simpleProductVo) ? (
            <DisabledButton>{t('product-diff:soldOut')}</DisabledButton>
          ) : checkPreSaleProduct(simpleProductVo) ? (
            <DisabledButton>{t('product-diff:preSale')}</DisabledButton>
          ) : cartNumber > 0 ? (
            <DisabledButton>{t('product-diff:added')}</DisabledButton>
          ) : (
            <Button
              className={classNames('flex-1 h-8 text-xs', 'pc:h-9 pc:text-sm')}
              onClick={addCart}
            >
              {t('common:goodsCard.addCart')}
            </Button>
          )}
          {showRemove && (
            <Button
              className={classNames(
                'flex-shrink-0 ml-2 w-8 h-8 border-black-7',
                'pc:ml-3 pc:w-18 pc:h-9 pc:text-sm'
              )}
              textClassName={classNames('hidden', 'pc:inline')}
              icon={<BlackLineIcon className={classNames('w-4 h-4', 'pc:hidden')} icon="garbage" />}
              dark={false}
              color="black-1"
              onClick={onRemove}
            >
              {t('common:remove')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
